import { type FormInstance, Form } from 'antd';
import { sortBy } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getAssetColumnCopy,
  getColumnName,
  getOrderedAncestors,
} from 'components/data/assets/assetColumnRenderers';
import { type AtlasGqlAsset } from 'types/atlas-graphql';

import {
  AssetOption,
  AssetOptionMeta,
  AssetOptionName,
  StyledSelect,
} from './DamageEditAsset.style';

const getAssetOptionName = (asset: AtlasGqlAsset): string => {
  return getColumnName(asset);
};

// maps and sorts assets
const mapAndSortAssets = (assets: AtlasGqlAsset[]) => {
  return sortBy(
    assets.map(asset => {
      const orderedAncestors = getOrderedAncestors(asset).filter(
        ancestor => ancestor?.assetType?.name !== 'Site'
      );
      const parent = orderedAncestors.slice(-1)[0];
      const grandparent = orderedAncestors.slice(-2, -1)[0];

      return {
        id: asset.id as string,
        name: getAssetOptionName(asset),
        assetTypeName: asset?.assetType?.name,
        parent: parent ? getAssetOptionName(parent) : undefined,
        parentTypeName: parent?.assetType?.name,
        grandparent: grandparent ? getAssetOptionName(grandparent) : undefined,
        grandparentTypeName: grandparent?.assetType?.name,
        sortKey: getAssetColumnCopy({ asset, separatorText: ' | ' }),
      };
    }),
    'sortKey'
  );
};

// The list shows name and ancestry; the closed select shows only `selectedLabel`.
const getAssetSelectOptions = (options: ReturnType<typeof mapAndSortAssets>) => {
  return options.map(option => {
    const { id, name, assetTypeName, parent, parentTypeName, grandparent, grandparentTypeName } =
      option;

    const label = assetTypeName ? [assetTypeName, name].filter(Boolean).join(' ') : name;
    const meta = [
      parent && parentTypeName ? `${parentTypeName} ${parent}` : parent,
      grandparent && grandparentTypeName ? `${grandparentTypeName} ${grandparent}` : grandparent,
    ]
      .filter(Boolean)
      .join(' | ');

    return {
      value: id,
      selectedLabel: label,
      label: (
        <AssetOption>
          <AssetOptionName>{label}</AssetOptionName>
          {meta && <AssetOptionMeta>{meta}</AssetOptionMeta>}
        </AssetOption>
      ),
    };
  });
};

type DamageEditAssetProps = {
  form: FormInstance;
  assetId?: string;
  selectedAsset?: AtlasGqlAsset | null;
  selectionScopeId?: string;
  onChange: (assetId: string | undefined) => void;
  autoSelectAsset?: boolean;
  potentialAssets: {
    likely: AtlasGqlAsset[];
    nearby: AtlasGqlAsset[];
  };
  externalLoading: boolean;
};

export default function DamageEditAsset({
  form,
  assetId,
  selectedAsset,
  selectionScopeId,
  onChange,
  autoSelectAsset = true,
  potentialAssets,
  externalLoading,
}: DamageEditAssetProps) {
  const { t } = useTranslation();
  const [hasUserSelected, setHasUserSelected] = useState<boolean>(false);
  const [internalAssetId, setInternalAssetId] = useState<string>();
  const onChangeRef = useRef(onChange);

  const { likelyAssetOptions, nearbyAssetOptions, shouldRenderMissingSelectedAssetOption } =
    useMemo(() => {
      const isSelectedAssetInPotentialAssets = assetId
        ? [...potentialAssets.likely, ...potentialAssets.nearby].some(asset => asset.id === assetId)
        : false;
      const selectedAssetForOptions =
        assetId && selectedAsset?.id === assetId && !isSelectedAssetInPotentialAssets
          ? selectedAsset
          : undefined;
      const likelyAssets = selectedAssetForOptions
        ? [selectedAssetForOptions, ...potentialAssets.likely]
        : potentialAssets.likely;

      return {
        likelyAssetOptions: mapAndSortAssets(likelyAssets),
        nearbyAssetOptions: mapAndSortAssets(potentialAssets.nearby),
        shouldRenderMissingSelectedAssetOption:
          !!assetId && !selectedAssetForOptions && !isSelectedAssetInPotentialAssets,
      };
    }, [assetId, potentialAssets, selectedAsset]);

  const missingSelectedAssetLabel = t('damage.deletedAsset', 'Deleted Asset');

  const missingSelectedAssetOptions =
    assetId && shouldRenderMissingSelectedAssetOption
      ? [
          {
            value: assetId,
            selectedLabel: missingSelectedAssetLabel,
            label: (
              <AssetOption>
                <AssetOptionName>{missingSelectedAssetLabel}</AssetOptionName>
              </AssetOption>
            ),
          },
        ]
      : [];

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    setHasUserSelected(false);
    setInternalAssetId(undefined);
    form.setFieldsValue({ assetId: undefined });
    onChangeRef.current(undefined);
  }, [form, selectionScopeId]);

  // An explicit assetId represents the current saved/selected asset and must
  // never be replaced by likely/nearby suggestions.
  useEffect(() => {
    if (hasUserSelected || !assetId || assetId === internalAssetId) {
      return;
    }

    setInternalAssetId(assetId);
    form.setFieldsValue({ assetId });
    onChange(assetId);
  }, [form, assetId, onChange, hasUserSelected, internalAssetId]);

  // handle changes to asset options
  useEffect(() => {
    // Skip if user has manually selected an asset or the caller supplied an
    // explicit asset. Preserve that asset even if it is not in the suggestions.
    if (hasUserSelected || assetId || !autoSelectAsset) {
      return;
    }

    // if current asset is not in likely options, update to first available
    const isCurrentAssetInLikely = likelyAssetOptions.some(option => option.id === internalAssetId);
    if (!isCurrentAssetInLikely) {
      const _assetId = likelyAssetOptions[0]?.id ?? nearbyAssetOptions[0]?.id;
      if (_assetId && _assetId !== internalAssetId) {
        setInternalAssetId(_assetId);
        form.setFieldsValue({ assetId: _assetId });
        onChange(_assetId);
      }
    }
  }, [
    form,
    assetId,
    onChange,
    autoSelectAsset,
    hasUserSelected,
    internalAssetId,
    likelyAssetOptions,
    nearbyAssetOptions,
  ]);

  // handle user selection
  const handleAssetChange = useCallback(
    (selectedAssetId: string) => {
      setHasUserSelected(true);
      setInternalAssetId(selectedAssetId);
      onChange(selectedAssetId);
    },
    [onChange]
  );

  return (
    <Form.Item
      label={t('damage.damageAsset', 'Damage Asset')}
      name="assetId"
      rules={[
        { required: true, message: t('damage.selectAssetRequired', 'Please select an Asset') },
      ]}
    >
      <StyledSelect
        className="testid-damage-asset-edit"
        allowClear={false}
        onChange={handleAssetChange}
        getPopupContainer={triggerNode => triggerNode.parentElement}
        loading={externalLoading}
        optionLabelProp="selectedLabel"
        options={[
          {
            label: t('damage.likelyAssets', 'Likely Assets'),
            options: [...missingSelectedAssetOptions, ...getAssetSelectOptions(likelyAssetOptions)],
          },
          {
            label: t('damage.nearbyAssets', 'Nearby Assets'),
            options: getAssetSelectOptions(nearbyAssetOptions),
          },
        ]}
      />
    </Form.Item>
  );
}
